import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, map, of, shareReplay, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthResponse, LoginRequest, RegisterStoreRequest } from '../models';
import { ThemeService } from './theme.service';
import { PlanService } from './plan.service';
import { BillingService } from './billing.service';

/** Token antigo (versões anteriores guardavam o JWT no localStorage); é apagado na abertura. */
const LEGACY_TOKEN_KEY = 'aggendai_token';
/** Dados da loja para exibição (nome, cor). Não é credencial: só indica que há sessão para restaurar. */
const STORE_KEY = 'aggendai_store';
const ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

/** Papel do usuário lido do JWT (só para exibir/ocultar telas; a API valida de novo). */
function roleFromToken(token: string | null): string | null {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    const role = payload[ROLE_CLAIM] ?? payload.role;
    return Array.isArray(role) ? role[0] : role ?? null;
  } catch {
    return null;
  }
}

/** Nome do usuário lido do JWT (claim "name"). */
function nameFromToken(token: string | null): string | null {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.name ?? null;
  } catch {
    return null;
  }
}

/**
 * Sessão da loja: o access token (JWT curto) fica só em memória; a sessão longa é o refresh token,
 * num cookie HttpOnly que o JavaScript não lê. Ao recarregar a página, a sessão é restaurada pelo cookie.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private theme = inject(ThemeService);
  private plans = inject(PlanService);
  private billing = inject(BillingService);
  private base = `${environment.apiUrl}/auth`;
  private refreshing$: Observable<string | null> | null = null;

  readonly token = signal<string | null>(null);
  readonly store = signal<AuthResponse | null>(this.readStore());
  readonly isAuthenticated = computed(() => !!this.token());
  readonly role = computed(() => roleFromToken(this.token()));
  readonly isOwner = computed(() => this.role() === 'Owner');
  /** Profissional logado com usuário e senha: vê só a própria área (Meus agendamentos e Perfil). */
  readonly isProfessional = computed(() => this.role() === 'Professional');
  readonly userName = computed(() => nameFromToken(this.token()));
  /** Tela inicial depois do login, conforme o perfil. */
  readonly homeRoute = computed(() => (this.isProfessional() ? '/meus-agendamentos' : '/dashboard'));

  constructor() {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(LEGACY_TOKEN_KEY);
  }

  loginProfessional(username: string, password: string) {
    return this.http.post<AuthResponse>(`${this.base}/professional-login`, { username, password }, { withCredentials: true }).pipe(
      tap((res) => this.persist(res)),
    );
  }

  login(req: LoginRequest) {
    return this.http.post<AuthResponse>(`${this.base}/login`, req, { withCredentials: true }).pipe(
      tap((res) => this.persist(res)),
    );
  }

  register(req: RegisterStoreRequest) {
    return this.http.post<AuthResponse>(`${this.base}/register`, req, { withCredentials: true }).pipe(
      tap((res) => this.persist(res)),
    );
  }

  loginWithGoogle(idToken: string) {
    return this.http.post<AuthResponse>(`${this.base}/google-login`, { idToken }, { withCredentials: true }).pipe(
      tap((res) => this.persist(res)),
    );
  }

  registerWithGoogle(idToken: string, storeName: string, segment: string) {
    return this.http.post<AuthResponse>(`${this.base}/google-register`, { idToken, storeName, segment }, { withCredentials: true }).pipe(
      tap((res) => this.persist(res)),
    );
  }

  /**
   * Pede um novo access token usando o cookie. Chamadas simultâneas compartilham a mesma requisição
   * (o refresh token é trocado a cada uso). Devolve null se a sessão acabou.
   */
  refresh(): Observable<string | null> {
    if (!this.refreshing$) {
      this.refreshing$ = this.http.post<AuthResponse>(`${this.base}/refresh`, {}, {
        withCredentials: true,
        headers: new HttpHeaders({ 'X-Refresh': '1' }),
      }).pipe(
        tap((res) => this.persist(res, false)),
        map((res) => res.token),
        // só 401/400 significam sessão encerrada; erro de rede ou limite (429/5xx) não apaga a sessão,
        // para a próxima recarga tentar de novo
        catchError((err: { status?: number }) => {
          if (err?.status === 401 || err?.status === 400) this.clear();
          return of(null);
        }),
        finalize(() => { this.refreshing$ = null; }),
        shareReplay(1),
      );
    }
    return this.refreshing$;
  }

  /** Na abertura do app: se havia sessão, recupera o access token pelo cookie antes das rotas. */
  restoreSession(): Observable<unknown> {
    return this.store() ? this.refresh() : of(null);
  }

  logout(): void {
    // revoga o refresh token no servidor e apaga o cookie (falha de rede não impede sair)
    this.http.post(`${this.base}/logout`, {}, {
      withCredentials: true,
      headers: new HttpHeaders({ 'X-Refresh': '1' }),
    }).subscribe({ error: () => undefined });
    this.endLocalSession();
  }

  /** Encerra só a sessão local (ex.: conta cancelada, o servidor já revogou o acesso). */
  endLocalSession(): void {
    this.clear();
    this.router.navigate(['/auth/login']);
  }

  private clear(): void {
    this.token.set(null);
    this.store.set(null);
    localStorage.removeItem(STORE_KEY);
    this.plans.clear();
    this.billing.clear();
    this.theme.reset();
  }

  private persist(res: AuthResponse, newSession = true): void {
    if (newSession) {
      this.plans.clear();
      this.billing.clear();
    }
    this.token.set(res.token);
    // sem o token: o que fica no navegador é só nome/cor da loja
    const { token: _, ...info } = res;
    this.store.set(res);
    localStorage.setItem(STORE_KEY, JSON.stringify(info));
    this.theme.applyPrimary(res.primaryColor);
  }

  private readStore(): AuthResponse | null {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as AuthResponse;
      // versões antigas guardavam o token junto: descarta
      return { ...parsed, token: '' };
    } catch {
      return null;
    }
  }
}
